import React, { useState, useEffect } from 'react';
import { Trophy, Clock, User, Phone, Mail, Medal, Timer, Award, RefreshCw, AlertCircle } from 'lucide-react';

const QuizResultsPage = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchResults = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Replace with your actual API endpoint
      const response = await fetch('https://form-1vqo.onrender.com/api/quiz/results');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Sort by score (descending), then by time taken (ascending) for ties
        const sortedResults = data.data.sort((a, b) => {
          if (b.score !== a.score) {
            return b.score - a.score; // Higher score first
          }
          return a.timeTaken - b.timeTaken; // Faster time first for ties
        });
        
        setResults(sortedResults);
      } else {
        throw new Error(data.message || 'Failed to fetch results');
      }
    } catch (err) {
      console.error('Error fetching results:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResults();
  }, []);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRankIcon = (index) => {
    if (index === 0) return <Trophy className="w-6 h-6 text-yellow-500" />;
    if (index === 1) return <Medal className="w-6 h-6 text-gray-400" />;
    if (index === 2) return <Award className="w-6 h-6 text-amber-600" />;
    return <span className="w-6 h-6 flex items-center justify-center text-sm font-bold text-gray-600">#{index + 1}</span>;
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600 bg-green-100';
    if (percentage >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-2 text-lg text-gray-600">Loading results...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Results</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={fetchResults}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-2">
            Quiz Results
          </h1>
          <p className="text-lg text-gray-600">
            Leaderboard - Ranked by Score & Speed
          </p>
          <div className="mt-4 flex items-center justify-center space-x-4 text-sm text-gray-500">
            <span className="flex items-center">
              <Trophy className="w-4 h-4 mr-1" />
              {results.length} Participants
            </span>
            <span className="flex items-center">
              <RefreshCw 
                className="w-4 h-4 mr-1 cursor-pointer hover:text-blue-600 transition-colors" 
                onClick={fetchResults}
              />
              Refresh
            </span>
          </div>
        </div>

        {/* Results Grid */}
        {results.length === 0 ? (
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">No Results Yet</h2>
            <p className="text-gray-600">Quiz results will appear here once participants complete the quiz.</p>
          </div>
        ) : (
          <div className="grid gap-4 md:gap-6">
            {results.map((result, index) => (
              <div
                key={`${result.phoneNumber}-${index}`}
                className={`bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 ${
                  index < 3 ? 'border-l-4 border-yellow-400' : ''
                }`}
              >
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-4 md:space-y-0">
                  {/* Rank and User Info */}
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      {getRankIcon(index)}
                    </div>
                    <div className="flex-grow">
                      <div className="flex items-center space-x-2 mb-1">
                        <User className="w-4 h-4 text-gray-500" />
                        <h3 className="text-lg font-semibold text-gray-800">
                          {result.fullName}
                        </h3>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-1 sm:space-y-0 text-sm text-gray-600">
                        <div className="flex items-center space-x-1">
                          <Phone className="w-3 h-3" />
                          <span>{result.phoneNumber}</span>
                        </div>
                        {result.email && (
                          <div className="flex items-center space-x-1">
                            <Mail className="w-3 h-3" />
                            <span className="truncate max-w-48">{result.email}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Score and Stats */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-6 w-full md:w-auto">
                    {/* Score */}
                    <div className="text-center">
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${getScoreColor(parseFloat(result.percentage))}`}>
                        <Trophy className="w-4 h-4 mr-1" />
                        {result.score}/{result.totalQuestions}
                      </div>
                      <div className="text-lg font-bold text-gray-800 mt-1">
                        {result.percentage}%
                      </div>
                    </div>

                    {/* Time */}
                    <div className="text-center">
                      <div className="flex items-center text-sm text-gray-600">
                        <Timer className="w-4 h-4 mr-1" />
                        <span>Time</span>
                      </div>
                      <div className="text-lg font-semibold text-gray-800">
                        {formatTime(result.timeTaken)}
                      </div>
                    </div>

                    {/* Date */}
                    <div className="text-center">
                      <div className="flex items-center text-sm text-gray-600">
                        <Clock className="w-4 h-4 mr-1" />
                        <span>Completed</span>
                      </div>
                      <div className="text-sm text-gray-800">
                        {formatDate(result.completedAt)}
                      </div>
                    </div>

                    {/* Status */}
                    <div className="text-center">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        result.status === 'completed' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-orange-100 text-orange-800'
                      }`}>
                        {result.status === 'completed' ? 'Completed' : 'Timeout'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary Stats */}
        {results.length > 0 && (
          <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Summary Statistics</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {results.length}
                </div>
                <div className="text-sm text-gray-600">Total Participants</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {(results.reduce((sum, r) => sum + parseFloat(r.percentage), 0) / results.length).toFixed(1)}%
                </div>
                <div className="text-sm text-gray-600">Average Score</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">
                  {Math.max(...results.map(r => parseFloat(r.percentage)))}%
                </div>
                <div className="text-sm text-gray-600">Highest Score</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {formatTime(Math.min(...results.map(r => r.timeTaken)))}
                </div>
                <div className="text-sm text-gray-600">Fastest Time</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizResultsPage;